/**
 * stay/models.ts — types for Module #4, the Roamer Stay™.
 *
 * A platform MODULE, not a core engine. Long-term accommodation intelligence
 * for digital nomads — living, not vacation travel. Honesty rules mirror the
 * other modules: listing attributes are nullable and never fabricated; the
 * Roamer Match Score™ scores only the components with real data and every
 * point is explained ("Why This?").
 */

// ─── Housing types ─────────────────────────────────────────────────────────────

export const HOUSING_TYPES = [
  'monthlyApartment',
  'studio',
  'condominium',
  'coliving',
  'extendedStayHotel',
  'guesthouse',
  'hostelLongStay',
  'privateRoom',
  'sharedHousing',
] as const;
export type HousingType = (typeof HOUSING_TYPES)[number] | string; // future types allowed

// ─── Housing profile ───────────────────────────────────────────────────────────

export interface HousingProfile {
  /** Listing id of where the user currently lives; null if none recorded. */
  currentHousingId: string | null;
  /** Monthly budget in the user's currency; null if unset. */
  monthlyBudget: number | null;
  budgetCurrency: string;
  preferredType: HousingType | null;
  /** Months; null if flexible/unset. */
  leaseLengthMonths: number | null;
  moveInDate: number | null;
  moveOutDate: number | null;
  /** Roommates support is a documented future feature. */
  hasPets: boolean;
  smokingPreference: 'nonSmoking' | 'smoking' | 'noPreference';
  requiresWorkspace: boolean;
  /** Minimum acceptable internet in Mbps; null if no requirement. */
  minInternetMbps: number | null;
  preferredNeighborhoods: string[];
}

export const DEFAULT_HOUSING_PROFILE: HousingProfile = {
  currentHousingId: null,
  monthlyBudget: null,
  budgetCurrency: 'USD',
  preferredType: null,
  leaseLengthMonths: null,
  moveInDate: null,
  moveOutDate: null,
  hasPets: false,
  smokingPreference: 'noPreference',
  requiresWorkspace: false,
  minInternetMbps: null,
  preferredNeighborhoods: [],
};

// ─── Listings ──────────────────────────────────────────────────────────────────

export interface StayAmenities {
  petFriendly: boolean | null;
  quiet: boolean | null;
  airConditioning: boolean | null;
  workspace: boolean | null;
  balcony: boolean | null;
  laundry: boolean | null;
  kitchen: boolean | null;
  gym: boolean | null;
  pool: boolean | null;
}

export const EMPTY_AMENITIES: StayAmenities = {
  petFriendly: null,
  quiet: null,
  airConditioning: null,
  workspace: null,
  balcony: null,
  laundry: null,
  kitchen: null,
  gym: null,
  pool: null,
};

export interface PricePoint {
  rentMonthly: number;
  at: number;
}

export interface StayListing {
  id: string;
  title: string;
  type: HousingType;
  city: string;
  country: string;
  neighborhood: string | null;
  /** Monthly rent in `currency`; null if not yet known. */
  rentMonthly: number | null;
  currency: string;
  /** Measured/claimed internet speed; null unknown. */
  internetMbps: number | null;
  /** True only when the speed was verified (community/provider), never assumed. */
  internetVerified: boolean;
  /** Walking distance to the user's key spots, minutes; null unknown. */
  walkabilityScore: number | null; // 0–100
  coworkingDistanceMin: number | null;
  safetyScore: number | null; // 0–100
  transitScore: number | null; // 0–100
  noiseLevel: 'quiet' | 'moderate' | 'loud' | null;
  /** Community rating 0–5; null when nobody has rated it. */
  communityRating: number | null;
  /** Count of WorkRoamers recommending it; 0 is a real zero only when known. */
  communityRecommendations: number | null;
  flexibleLease: boolean | null;
  available: boolean;
  amenities: StayAmenities;
  /** How the listing entered the system. */
  source: 'manual' | 'provider' | 'community';
  priceHistory: PricePoint[];
  addedAt: number;
  url: string | null;
}

// ─── Search filters ────────────────────────────────────────────────────────────

export interface StaySearchFilters {
  city?: string;
  maxMonthlyBudget?: number;
  minInternetMbps?: number;
  verifiedInternetOnly?: boolean;
  maxCoworkingDistanceMin?: number;
  neighborhood?: string;
  minSafetyScore?: number;
  minTransitScore?: number;
  minWalkabilityScore?: number;
  minCommunityRating?: number;
  petFriendly?: boolean;
  quiet?: boolean;
  airConditioning?: boolean;
  workspace?: boolean;
  balcony?: boolean;
  laundry?: boolean;
  kitchen?: boolean;
  gym?: boolean;
  pool?: boolean;
  flexibleLease?: boolean;
  type?: HousingType;
  availableOnly?: boolean;
}

// ─── Roamer Match Score™ ───────────────────────────────────────────────────────

export interface MatchReason {
  text: string;
  /** Positive or negative contribution to the score. */
  delta: number;
}

export interface RoamerMatchScore {
  listingId: string;
  /** 0–100; null when NO scorable data exists — never a fabricated number. */
  score: number | null;
  stars: number | null; // 1–5 derived from score
  /** "Why This?" — every scored component explained; empty ⇒ score null. */
  reasons: MatchReason[];
  /** Components skipped because the listing lacks the data. */
  unknowns: string[];
}

// ─── Comparison ────────────────────────────────────────────────────────────────

export interface StayComparisonRow {
  label: string;
  values: (string | number | null)[];
}

export interface StayComparison {
  listingIds: string[];
  titles: string[];
  rows: StayComparisonRow[];
  matchScores: (number | null)[];
}

// ─── Housing intelligence ──────────────────────────────────────────────────────

/** Derived exclusively from recorded listings/history — null when no data. */
export interface HousingIntelligence {
  city: string;
  listingCount: number;
  averageRent: number | null;
  averageRentByNeighborhood: { neighborhood: string; averageRent: number; listingCount: number }[];
  /** 'rising' | 'falling' | 'stable' from recorded price history; null without ≥2 points. */
  priceTrend: 'rising' | 'falling' | 'stable' | null;
  averageInternetMbps: number | null;
  verifiedInternetShare: number | null;
  quietShare: number | null;
  /** Sum of known community recommendations — a popularity signal, not a claim. */
  nomadRecommendations: number | null;
}

// ─── Favorites, shortlists, alerts ─────────────────────────────────────────────

export interface Shortlist {
  id: string;
  name: string;
  listingIds: string[];
  createdAt: number;
}

export interface SharedListing {
  listingId: string;
  title: string;
  city: string;
  rentMonthly: number | null;
  currency: string;
  url: string | null;
  sharedAt: number;
}

// ─── Providers (future integrations — interfaces only) ────────────────────────

export const STAY_PROVIDER_KINDS = [
  'apartments',
  'coliving',
  'extendedStay',
  'verifiedCommunityListings',
  'realEstateApi',
  'propertyManager',
  'housingMarketData',
] as const;
export type StayProviderKind = (typeof STAY_PROVIDER_KINDS)[number];

export interface StayProvider {
  id: string;
  name: string;
  kind: StayProviderKind;
  fetchListings(city: string): Promise<StayListing[]>;
}

// ─── Stay records (accommodation tracking — Build 2.03.0) ─────────────────────
// Manual tracking only: booking/hotel/Airbnb integrations do NOT exist and are
// never fabricated. Every record is entered by the roamer.

export const PAYMENT_STATUSES = ['paid', 'pending', 'unpaid'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** One accommodation the roamer stayed / stays / will stay at. */
export interface StayRecord {
  id: string;
  propertyName: string;
  /** HousingType or free text. */
  type: string;
  city: string;
  country: string;
  address: string | null;
  host: string | null;
  bookingRef: string | null;
  /** Total recorded price for the stay; null when not recorded. */
  price: number | null;
  currency: string;
  paymentStatus: PaymentStatus | null;
  /** Epoch ms. */
  checkIn: number;
  /** Epoch ms; null = open-ended. */
  checkOut: number | null;
  /** 1–5 ratings; null = not assessed (unknown ≠ bad). */
  internetQuality: number | null;
  workspaceSuitability: number | null;
  quietScore: number | null;
  walkability: number | null;
  safetyNotes: string | null;
  personalNotes: string | null;
  addedAt: number;
}

export type NewStayRecordInput = Omit<StayRecord, 'id' | 'addedAt'>;

export interface StayTimeline {
  past: StayRecord[];
  current: StayRecord | null;
  future: StayRecord[];
}

/** Dashboard snapshot — derived, never stored. */
export interface StayDashboard {
  currentStay: StayRecord | null;
  /** Days until checkOut; null when no current stay or open-ended. */
  daysRemaining: number | null;
  /** Total planned nights of the current stay; null when open-ended. */
  stayDurationDays: number | null;
  /** Plain-English reminder strings; null when not applicable. */
  extensionReminder: string | null;
  moveReminder: string | null;
}

/** Base intelligence + history — derived exclusively from recorded stays; null when unscorable. */
export interface StayStats {
  totalStays: number;
  countriesStayed: string[];
  citiesStayed: string[];
  longestStayDays: number | null;
  shortestStayDays: number | null;
  averageStayLengthDays: number | null;
  /** Current stay economics (recorded price only). */
  costPerNight: number | null;
  costPerMonth: number | null;
  /** Sum of all recorded stay prices (single-currency sum per currency). */
  runningTotalByCurrency: { currency: string; total: number }[];
  /** Average monthly housing cost across completed stays with price + duration. */
  averageMonthlyHousingCost: number | null;
  /** Roamer Stay Score™ 0–100 over rated components only; null when nothing rated. */
  stayScore: number | null;
}

/** Privacy for stay history — both OFF by default. */
export interface StayPrivacy {
  shareStayHistory: boolean;
  shareCurrentLocation: boolean;
}

export const DEFAULT_STAY_PRIVACY: StayPrivacy = {
  shareStayHistory: false,
  shareCurrentLocation: false,
};

// ─── Export ────────────────────────────────────────────────────────────────────

export interface StayExport {
  exportedAt: number;
  profile: HousingProfile;
  listings: StayListing[];
  favoriteListingIds: string[];
  favoriteNeighborhoods: string[];
  shortlists: Shortlist[];
  stayRecords: StayRecord[];
  stayPrivacy: StayPrivacy;
}
